import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Banner, Button, DataTable, EmptyState, Input, ListCard, PageHeader, Select, StatusBadge } from "../components/ui";
import { Icon } from "../components/icons";
import { useSession } from "../context/Session";
import { checkOut, createPreregistration, dispatchVisitNotifications, getHistory, getVisit, listVisitNotifications, listVisits, resendInvitation } from "../lib/api";
import { notificationStatusLabel } from "../lib/emailStatus";
import { supabase } from "../lib/supabase";

export function VisitorList() {
  const { orgId, can } = useSession();
  const [params] = useSearchParams();
  const [visits, setVisits] = useState([]);
  const [sites, setSites] = useState([]);
  const [types, setTypes] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [filters, setFilters] = useState({ status: params.get("status") || "", siteId: "", typeId: "", hostId: "", q: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    supabase.from("sites").select("id, name").eq("organization_id", orgId).then(({ data }) => setSites(data || []));
    supabase.from("visitor_types").select("id, name").eq("organization_id", orgId).then(({ data }) => setTypes(data || []));
    supabase.from("hosts").select("id, first_name, last_name").eq("organization_id", orgId).then(({ data }) => setHosts(data || []));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    listVisits(orgId, filters).then(({ data, error: listError }) => {
      if (listError) setError(listError.message);
      const query = filters.q.toLowerCase();
      setVisits((data || []).filter((visit) => {
        const name = `${visit.visitors?.first_name || ""} ${visit.visitors?.last_name || ""} ${visit.visitors?.company || ""}`.toLowerCase();
        return !query || name.includes(query);
      }));
      setLoading(false);
    });
  }, [orgId, filters]);

  const view = params.get("view");
  const shown = visits.filter((visit) => {
    if (view === "onsite") return visit.status === "checked_in";
    if (view === "expected") return ["expected", "invited", "approved", "pending_approval"].includes(visit.status);
    if (view === "history") return ["checked_out", "expired", "cancelled", "rejected", "denied"].includes(visit.status);
    return true;
  });

  const title = view === "expected" ? "Expected visitors" : view === "onsite" ? "On site" : view === "history" ? "Visit history" : "Visitors";
  return (
    <div>
      <PageHeader title={title} subtitle="Search, filter, and open a visit." actions={can("visitors.create") ? <Link to="/visitors/new"><Button><Icon name="plus" className="h-4 w-4" /> Invite visitor</Button></Link> : null} />
      {error ? <Banner>{error}</Banner> : (
        <ListCard title="All visits" count={loading ? "Loading visits" : `${shown.length} ${shown.length === 1 ? "visit" : "visits"}`}>
          <div className="grid gap-3 border-b border-slate-100 px-5 py-4 md:grid-cols-5">
            <Input label="Search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
            <Select label="Status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">All</option>
              <option value="checked_in">On site</option>
              <option value="expected">Expected</option>
              <option value="pending_approval">Pending</option>
              <option value="checked_out">Checked out</option>
            </Select>
            <Select label="Site" value={filters.siteId} onChange={(e) => setFilters({ ...filters, siteId: e.target.value })}>
              <option value="">All</option>
              {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
            </Select>
            <Select label="Type" value={filters.typeId} onChange={(e) => setFilters({ ...filters, typeId: e.target.value })}>
              <option value="">All</option>
              {types.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
            </Select>
            <Select label="Host" value={filters.hostId} onChange={(e) => setFilters({ ...filters, hostId: e.target.value })}>
              <option value="">All</option>
              {hosts.map((host) => <option key={host.id} value={host.id}>{host.first_name} {host.last_name}</option>)}
            </Select>
          </div>
          <DataTable
            embedded
            loading={loading}
            rows={shown}
            empty={{ icon: "visits", title: "No matching visits", body: "Adjust the filters or invite a visitor.", action: can("visitors.create") ? <Link to="/visitors/new"><Button><Icon name="plus" className="h-4 w-4" /> Invite visitor</Button></Link> : null }}
            columns={[
              { key: "visitor", label: "Visitor", render: (visit) => <Link className="font-semibold text-brand-ink hover:text-brand-blue" to={`/visitors/${visit.id}`}>{visit.visitors?.first_name} {visit.visitors?.last_name}</Link> },
              { key: "company", label: "Company", render: (visit) => visit.visitors?.company || "—" },
              { key: "host", label: "Host", render: (visit) => `${visit.employees?.first_name || ""} ${visit.employees?.last_name || ""}`.trim() || "—" },
              { key: "site", label: "Site", render: (visit) => visit.sites?.name || "—" },
              { key: "status", label: "Status", render: (visit) => <StatusBadge status={visit.status} /> },
            ]}
          />
        </ListCard>
      )}
    </div>
  );
}

export function InviteVisitor() {
  const { orgId } = useSession();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", purpose: "", notes: "", expected_arrival: "", expected_departure: "", equipment_declaration: "", restricted_area: "" });
  const [refs, setRefs] = useState({ sites: [], hosts: [], types: [], companies: [] });
  const [ids, setIds] = useState({ site_id: "", host_employee_id: "", visitor_type_id: "", contractor_company_id: "" });
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!orgId) return;
    Promise.all([
      supabase.from("sites").select("id, name").eq("organization_id", orgId),
      supabase.from("hosts").select("id, first_name, last_name").eq("organization_id", orgId),
      supabase.from("visitor_types").select("id, name, is_contractor").eq("organization_id", orgId),
      supabase.from("contractor_companies").select("id, name").eq("organization_id", orgId),
    ]).then(([sites, hosts, types, companies]) => {
      setRefs({ sites: sites.data || [], hosts: hosts.data || [], types: types.data || [], companies: companies.data || [] });
    });
  }, [orgId]);

  function setField(key, value) { setForm((current) => ({ ...current, [key]: value })); }

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const { data, error: rpcError } = await createPreregistration({
      ...form,
      ...ids,
      organization_id: orgId,
      expected_arrival: form.expected_arrival ? new Date(form.expected_arrival).toISOString() : null,
      expected_departure: form.expected_departure ? new Date(form.expected_departure).toISOString() : null,
      induction_required: refs.types.find((type) => type.id === ids.visitor_type_id)?.is_contractor || false,
    });
    setBusy(false);
    if (rpcError) { setError(rpcError.message); return; }
    setResult(data);
    if (data?.visit_id) {
      const dispatched = await dispatchVisitNotifications(data.visit_id);
      if (dispatched.error) setError(dispatched.error.message || "Visit saved. Email dispatch failed.");
    }
  }

  return (
    <form onSubmit={onSubmit}>
      <PageHeader title="Invite visitor" subtitle="Approval is requested when the visitor type requires it. The QR token stays on the server." />
      {error ? <div className="mb-4"><Banner>{error}</Banner></div> : null}
      {result ? <div className="mb-4"><Banner tone="info">Visit {result.visit_id} is {result.status}. {result.invitation_id ? "Invitation email was queued." : "An approver must approve before the invitation is issued."}</Banner></div> : null}
      <div className="max-w-3xl space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="grid gap-4 md:grid-cols-2">
        <Input label="First name" required value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} />
        <Input label="Last name" required value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} />
        <Input label="Email" type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} />
        <Input label="Phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
        <Input label="Company" value={form.company} onChange={(e) => setField("company", e.target.value)} />
        <Select label="Visitor type" required value={ids.visitor_type_id} onChange={(e) => setIds({ ...ids, visitor_type_id: e.target.value })}>
          <option value="">Select</option>
          {refs.types.map((type) => <option key={type.id} value={type.id}>{type.name}</option>)}
        </Select>
        <Select label="Host" required value={ids.host_employee_id} onChange={(e) => setIds({ ...ids, host_employee_id: e.target.value })}>
          <option value="">Select</option>
          {refs.hosts.map((host) => <option key={host.id} value={host.id}>{host.first_name} {host.last_name}</option>)}
        </Select>
        <Select label="Site" required value={ids.site_id} onChange={(e) => setIds({ ...ids, site_id: e.target.value })}>
          <option value="">Select</option>
          {refs.sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
        </Select>
        <Input label="Arrival" type="datetime-local" value={form.expected_arrival} onChange={(e) => setField("expected_arrival", e.target.value)} />
        <Input label="Departure" type="datetime-local" value={form.expected_departure} onChange={(e) => setField("expected_departure", e.target.value)} />
        <Input label="Purpose" required value={form.purpose} onChange={(e) => setField("purpose", e.target.value)} />
        <Input label="Restricted area" value={form.restricted_area} onChange={(e) => setField("restricted_area", e.target.value)} />
      </div>
      <Input label="Equipment declaration" value={form.equipment_declaration} onChange={(e) => setField("equipment_declaration", e.target.value)} />
      <Input label="Notes" value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
      {refs.companies.length ? (
        <Select label="Contractor company" value={ids.contractor_company_id} onChange={(e) => setIds({ ...ids, contractor_company_id: e.target.value })}>
          <option value="">None</option>
          {refs.companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
        </Select>
      ) : null}
      <Button type="submit" disabled={busy}>{busy ? "Saving" : "Create visit"}</Button>
      </div>
    </form>
  );
}

export function VisitDetail() {
  const { can } = useSession();
  const id = window.location.pathname.split("/").pop();
  const [visit, setVisit] = useState(null);
  const [history, setHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [invitationId, setInvitationId] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function loadNotifications() {
    const { data } = await listVisitNotifications(id);
    setNotifications(data || []);
    const { data: invitation } = await supabase.from("invitations").select("id").eq("visit_id", id).order("created_at", { ascending: false }).limit(1).maybeSingle();
    setInvitationId(invitation?.id || "");
  }

  useEffect(() => {
    getVisit(id).then(({ data, error: loadError }) => {
      if (loadError) setError(loadError.message);
      setVisit(data);
    });
    getHistory(id).then(({ data }) => setHistory(data || []));
    loadNotifications();
  }, [id]);

  async function checkout() {
    setBusy(true);
    const { data, error: rpcError } = await checkOut(id);
    setBusy(false);
    if (rpcError) { setError(rpcError.message); return; }
    setMessage(`Checked out. Badge ${data.badge}. Access control: ${data.access_control}.`);
    const refreshed = await getVisit(id);
    setVisit(refreshed.data);
    const hist = await getHistory(id);
    setHistory(hist.data || []);
    await dispatchVisitNotifications(id);
    await loadNotifications();
  }

  async function resend() {
    if (!invitationId) return;
    setBusy(true);
    setError("");
    const { data, error: rpcError } = await resendInvitation(invitationId);
    if (rpcError) {
      setBusy(false);
      setError(rpcError.message);
      return;
    }
    if (data?.notification_id) await dispatchVisitNotifications(id);
    setBusy(false);
    setMessage("Invitation queued again.");
    await loadNotifications();
  }

  if (error) return <Banner>{error}</Banner>;
  if (!visit) return <p className="text-sm">Loading visit…</p>;
  return (
    <div>
      <PageHeader title={`${visit.visitors?.first_name} ${visit.visitors?.last_name}`} subtitle={visit.purpose} actions={<StatusBadge status={visit.status} />} />
      {message ? <div className="mb-4"><Banner tone="info">{message}</Banner></div> : null}
      <div className="flex flex-wrap gap-2">
        {visit.status === "checked_in" && can("visitors.checkout") ? <Button disabled={busy} onClick={checkout}>{busy ? "Checking out" : "Check out"}</Button> : null}
        {invitationId && can("visitors.create") && ["approved", "invited", "expected"].includes(visit.status) ? <Button variant="secondary" disabled={busy} onClick={resend}>{busy ? "Resending" : "Resend invitation"}</Button> : null}
      </div>
      <div className="mt-4 grid gap-3 xl:grid-cols-3">
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm xl:col-span-1">
          <h2 className="text-base font-semibold">Visit</h2>
          <dl className="mt-4 space-y-3 text-sm">
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Company</dt><dd className="mt-0.5 font-semibold">{visit.visitors?.company || "—"}</dd></div>
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Host</dt><dd className="mt-0.5 font-semibold">{visit.employees?.first_name} {visit.employees?.last_name}</dd></div>
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Site</dt><dd className="mt-0.5 font-semibold">{visit.sites?.name || "—"}</dd></div>
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Type</dt><dd className="mt-0.5 font-semibold">{visit.visitor_types?.name || "—"}</dd></div>
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Equipment</dt><dd className="mt-0.5">{visit.equipment_declaration || "None declared"}</dd></div>
            <div><dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Restricted area</dt><dd className="mt-0.5">{visit.restricted_area || "None"}</dd></div>
          </dl>
        </section>
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold">Notifications</h2>
          <p className="mt-0.5 text-xs text-slate-500">Email queued for this visit</p>
          {notifications.length === 0 ? <div className="mt-4"><EmptyState compact icon="email" title="No email yet" body="Notifications appear here after the visit is saved." /></div> : (
            <ul className="mt-4 divide-y divide-slate-100 text-sm">
              {notifications.map((row) => (
                <li key={row.id} className="py-2.5">
                  <span className="block font-semibold capitalize">{row.template_key.replaceAll("_", " ")}</span>
                  <span className="text-xs text-slate-500">{notificationStatusLabel(row.status)}{row.sent_at ? ` · ${new Date(row.sent_at).toLocaleString()}` : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold">History</h2>
          <p className="mt-0.5 text-xs text-slate-500">Status changes for this visit</p>
          {history.length === 0 ? <div className="mt-4"><EmptyState compact icon="audit" title="No history yet" body="Status changes will be listed here." /></div> : (
            <ol className="mt-4 divide-y divide-slate-100 text-sm">
              {history.map((row) => (
                <li key={row.id} className="py-2.5">
                  <span className="block font-semibold">{row.from_status || "created"} → {row.to_status}</span>
                  <span className="text-xs text-slate-500">{row.source}{row.comment ? ` · ${row.comment}` : ""}</span>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
